import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { LoadingButton } from "./Spinner";
import { useToast } from "./Toast";

interface Props {
  collectionId: string;
  open: boolean;
  onClose: () => void;
}

type Format = "postman" | "insomnia" | "bruno";

// One modal, three importers. Postman + Insomnia both arrive as a
// single JSON file; Bruno arrives as a folder of .bru files. We auto-
// detect when possible — Postman exports carry an `info.schema` URL,
// Insomnia carries `_type: "export"`.
export default function ImportModal({ collectionId, open, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const [format, setFormat] = useState<Format>("postman");
  const [payload, setPayload] = useState<unknown>(null);
  const [summary, setSummary] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const dirRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) {
      setPayload(null); setSummary(""); setError(null); setFormat("postman");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleJsonFile = async (file: File) => {
    setError(null);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      // Auto-detect format from well-known markers.
      let detected: Format = format;
      if (parsed?.info?.schema && String(parsed.info.schema).includes("postman")) detected = "postman";
      else if (parsed?._type === "export" && Array.isArray(parsed.resources)) detected = "insomnia";
      setFormat(detected);
      setPayload(parsed);
      const reqCount = detected === "postman"
        ? countPostman(parsed.item)
        : detected === "insomnia"
        ? (parsed.resources as Array<{ _type: string }>).filter((r) => r._type === "request").length
        : 0;
      setSummary(`${reqCount} request${reqCount === 1 ? "" : "s"} ready to import.`);
    } catch (e) {
      setError(`Couldn't read ${file.name}: ${(e as Error).message}`);
      setPayload(null);
      setSummary("");
    }
  };

  const handleBruDir = async (files: FileList) => {
    setError(null);
    setFormat("bruno");
    const arr: Array<{ path: string; text: string }> = [];
    let scanned = 0;
    for (const f of Array.from(files)) {
      if (!f.name.toLowerCase().endsWith(".bru")) continue;
      const text = await f.text();
      const path = (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;
      arr.push({ path, text });
      scanned++;
    }
    setPayload(arr);
    setSummary(`${scanned} .bru file${scanned === 1 ? "" : "s"} ready to import.`);
  };

  const submit = useMutation({
    mutationFn: () => api.imports.run({ collection_id: collectionId, format, payload }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["requests", collectionId] });
      qc.invalidateQueries({ queryKey: ["folders", collectionId] });
      const warn = r.warnings.length ? ` (${r.warnings.length} warnings)` : "";
      toast.success(`Imported ${r.created} request${r.created === 1 ? "" : "s"}${warn}`);
      if (r.warnings.length) {
        // Show the first few warnings inline so the user sees what was skipped.
        setError("Some items were skipped:\n" + r.warnings.slice(0, 5).join("\n"));
        return;
      }
      onClose();
    },
    onError: (e) => setError(String((e as Error).message)),
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] grid place-items-center p-4 bg-[color:var(--backdrop)]" onClick={onClose}>
      <div className="card w-full max-w-xl overflow-hidden flex flex-col shadow-[var(--shadow-strong)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <h2 className="text-base font-semibold">Import requests</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="h-8 w-8 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2">×</button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm muted">Import an existing collection from Postman, Insomnia, or Bruno. Folder structure and auth carry over. Existing requests in this collection aren't touched.</p>

          <div className="space-y-2">
            <label className="label">Format</label>
            <select
              className="input mono w-72"
              value={format}
              onChange={(e) => { setFormat(e.target.value as Format); setPayload(null); setSummary(""); }}
            >
              <option value="postman">Postman v2.1 collection (.json)</option>
              <option value="insomnia">Insomnia v4 export (.json)</option>
              <option value="bruno">Bruno (folder of .bru files)</option>
            </select>
          </div>

          {format !== "bruno" && (
            <div className="space-y-2">
              <label className="label">Collection file</label>
              <input
                ref={fileRef}
                type="file"
                accept=".json,application/json"
                className="block text-sm"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleJsonFile(f); }}
              />
            </div>
          )}

          {format === "bruno" && (
            <div className="space-y-2">
              <label className="label">Bruno folder</label>
              {/* The webkitdirectory attribute is non-standard but
                  supported in every modern browser; React's typings
                  don't have it, hence the spread cast. */}
              <input
                ref={dirRef}
                type="file"
                className="block text-sm"
                {...({ webkitdirectory: "true", directory: "true" } as Record<string, string>)}
                onChange={(e) => { if (e.target.files) handleBruDir(e.target.files); }}
              />
              <p className="text-xs muted">Pick the root folder of your Bruno collection. Sub-folders become sidebar folders.</p>
            </div>
          )}

          {summary && <div className="text-sm">{summary}</div>}
          {error && <pre className="rounded-md border border-danger/40 bg-danger/[0.06] p-2 text-xs whitespace-pre-wrap text-danger">{error}</pre>}
        </div>
        <div className="border-t border-border px-4 py-3 flex items-center justify-end gap-2">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <LoadingButton
            className="btn-primary"
            pending={submit.isPending}
            disabled={!payload}
            onClick={() => submit.mutate()}
          >Import</LoadingButton>
        </div>
      </div>
    </div>
  );
}

function countPostman(items: unknown): number {
  if (!Array.isArray(items)) return 0;
  let n = 0;
  for (const it of items as Array<{ item?: unknown; request?: unknown }>) {
    if (Array.isArray(it.item)) n += countPostman(it.item);
    else if (it.request) n++;
  }
  return n;
}
